import { Caption, QualityLevel, VisualQuality } from '../types/player';
import { VideoPlayerActionContext } from '../types';
import { Entry } from '@/scripts/models/utility-types';

type Ctx = VideoPlayerActionContext;

/**
 * The goal of Evt interface is to patch some of the EventParams
 * provided by @types/jwplayer.
 *
 * The provided EventParams do not match the actual event produced
 * by the events in @viddsee/jwplayer@8.20.1 (commercial) version.
 */
export interface Evt extends Omit<jwplayer.EventParams, (
  | 'captionsChanged'
  | 'captionsList'
  | 'levelsChanged'
  | 'volume'
  | 'visualQuality'
)> {
  captionsChanged: { tracks: Caption[]; track: number };
  captionsList: { tracks: Caption[]; track: number };
  levelsChanged: jwplayer.EventParams['levelsChanged'] & { levels: QualityLevel[] };
  volume: { volume: number };
  visualQuality: VisualQuality;
  viewable: {
    /**
     * Possible Values:
     * • 0: The player is below 50% or is in an inactive tab
     * • 1: The player is at least 50% in view and is in the active tab
     */
    viewable: 0 | 1;
  };
}

export const eventListeners = {
  ready: (context: Ctx): void => {
    context.commit('set', ['state', context.getters.player!.getState()]);
  },
  // Normal state
  idle: (context: Ctx): void => {
    context.dispatch('updateStates');
  },
  play: (context: Ctx): void => {
    context.commit('set', ['state', context.getters.player!.getState()]);
    context.dispatch('updateStates');
  },
  pause: (context: Ctx): void => {
    context.commit('set', ['state', context.getters.player!.getState()]);
  },
  complete: (context: Ctx): void => {
    context.commit('set', ['state', context.getters.player!.getState()]);
  },
  buffer: (context: Ctx): void => {
    context.commit('set', ['state', context.getters.player!.getState()]);
  },

  // Progress
  time: (context: Ctx, { position }: Evt['time']): void => {
    context.commit('set', ['position', position]);
  },

  // Volume controls
  mute: (context: Ctx, { mute }: Evt['mute']): void => {
    context.commit('set', ['mute', mute]);
  },
  volume: (context: Ctx, { volume }: Evt['volume']): void => {
    context.commit('set', ['volume', volume]);
  },

  // Quality
  levelsChanged: (context: Ctx, { levels, currentQuality }: Evt['levelsChanged']): void => {
    context.commit('set', ['qualityLevels', levels]);
    context.commit('set', ['currentQuality', currentQuality]);
  },
  visualQuality(context: Ctx, visualQuality: Evt['visualQuality']): void {
    context.commit('set', ['visualQuality', visualQuality]);
  },

  // Captions
  captionsChanged: (context: Ctx, { track }: Evt['captionsChanged']): void => {
    context.commit('set', ['currentCaptions', track]);
  },
  captionsList: (context: Ctx, { tracks }: Evt['captionsList']): void => {
    context.commit('set', ['captionsList', tracks]);
  },

  // Viewability
  viewable: (context: Ctx, { viewable }: Evt['viewable']): void => {
    context.commit('set', ['viewable', viewable === 1]);
  },
};

export type EventListeners = typeof eventListeners;

const eventListenerEntries = Object.entries(eventListeners) as Entry<EventListeners>[];

const extractContext = (context: Ctx): null | {
  playerHandlerMaps: Record<string, (param: any) => void>;
  player: jwplayer.JWPlayer;
} => {
  const player = context.getters.player;
  if (!player) return null;

  return {
    player,
    playerHandlerMaps: context.state.$_internal.playerHandlerMaps,
  };
};

export const attachEventListeners = (context: Ctx): void => {
  const extract = extractContext(context);
  if (!extract) return;

  const { player, playerHandlerMaps } = extract;

  for (const [listenerName, handler] of eventListenerEntries) {
    const boundHandler = (handler as any).bind(undefined, context);

    playerHandlerMaps[listenerName] = boundHandler;
    player.on(listenerName as any, boundHandler);
  }
};

export const detachEventListeners = (context: Ctx): void => {
  const extract = extractContext(context);
  if (!extract) return;

  const { player, playerHandlerMaps } = extract;

  for (const listenerName in playerHandlerMaps) {
    const boundHandler = playerHandlerMaps[listenerName];
    player.off(listenerName as any, boundHandler);
  }
};
